import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SpotServices from '../../services/externalServices/TourPackageServices/spotServices';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box
} from '@mui/material';

const AddSpot = () => {
  const navigate = useNavigate();
  const [spot, setSpot] = useState({
    name: { en: '', kn: '' },
    audioGuide: {
      en: { isEnabled: false, name: '', short: {}, medium: {}, long: {} },
      kn: { isEnabled: false, name: '', short: {}, medium: {}, long: {} }
    },
    location: { latitude: 0, longitude: 0 },
    description: '',
    thumbnailImage: '',
    isEnabled: true,
    internalComments:''
  });
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [availableLanguages] = useState(['en', 'kn']);
  const [error, setError] = useState(null);

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSpot(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNestedChange = (path, value) => {
    const keys = path.split('.');
    setSpot(prev => {
      const newState = { ...prev };
      let current = newState;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]] = { ...current[keys[i]] };
      }
      
      current[keys[keys.length - 1]] = value;
      return newState;
    });
  };

  const handleLocationChange = (field, value) => {
    setSpot(prev => ({
      ...prev,
      location: {
        ...prev.location,
        [field]: value
      }
    }));
  };

  const handleAudioGuideChange = (field, value) => {
    setSpot(prev => ({
      ...prev,
      audioGuide: {
        ...prev.audioGuide,
        [selectedLanguage]: {
          ...prev.audioGuide[selectedLanguage],
          [field]: value
        }
      }
    }));
  };

  const handleAudioUrlChange = (duration, field, value) => {
    setSpot(prev => ({
      ...prev,
      audioGuide: {
        ...prev.audioGuide,
        [selectedLanguage]: {
          ...prev.audioGuide[selectedLanguage],
          [duration]: {
            ...prev.audioGuide[selectedLanguage][duration],
            [field]: value
          }
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await SpotServices.addSpots(spot);
      navigate('/spots');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>Add New Spot</Typography>
      
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          Error: {error}
        </Typography>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Language Selector Dropdown */}
          <Grid item xs={12}>
            <TextField
              select
              label="Select Language"
              value={selectedLanguage}
              onChange={handleLanguageChange}
              fullWidth
              variant="outlined"
            >
              {availableLanguages.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang === 'en' ? 'English' : 'Kannada'} ({lang.toUpperCase()})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Basic Fields */}
          <Grid item xs={12}>
            <TextField
              label={`Name (${selectedLanguage.toUpperCase()})`}
              value={spot.name[selectedLanguage]}
              onChange={(e) => handleNestedChange(`name.${selectedLanguage}`, e.target.value)}
              fullWidth
              variant="outlined"
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              value={spot.description}
              onChange={(e) => handleChange({ target: { name: 'description', value: e.target.value } })}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Thumbnail Image URL"
              value={spot.thumbnailImage}
              onChange={(e) => handleChange({ target: { name: 'thumbnailImage', value: e.target.value } })}
              fullWidth
              variant="outlined"
            />
            {spot.thumbnailImage && (
              <Box mt={2}>
                <img 
                  src={spot.thumbnailImage} 
                  alt="Preview" 
                  style={{ maxHeight: 200, maxWidth: '100%' }}
                  onError={(e) => e.target.style.display = 'none'}
                />
              </Box>
            )}
          </Grid>

          {/* Location Fields */}
          <Grid item xs={6}>
            <TextField
              label="Latitude"
            //   type="number"
              value={spot.location.latitude}
              onChange={(e) => handleLocationChange('latitude', parseFloat(e.target.value))}
              fullWidth
              variant="outlined"
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Longitude"
            //   type="number"
              value={spot.location.longitude}
              onChange={(e) => handleLocationChange('longitude', parseFloat(e.target.value))}
              fullWidth
              variant="outlined"
              required
            />
          </Grid>

          {/* Audio Guide Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {selectedLanguage.toUpperCase()} Audio Guide
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={spot.audioGuide[selectedLanguage]?.isEnabled || false}
                  onChange={(e) => handleAudioGuideChange('isEnabled', e.target.checked)}
                />
              }
              label="Enable Audio Guide"
            />

            {spot.audioGuide[selectedLanguage]?.isEnabled && (
              <Box mt={2} sx={{ border: '1px solid #eee', p: 2, borderRadius: 1 }}>
                <TextField
                  label="Guide Name"
                  value={spot.audioGuide[selectedLanguage]?.name || ''}
                  onChange={(e) => handleAudioGuideChange('name', e.target.value)}
                  fullWidth
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                {['short', 'medium', 'long'].map((duration) => (
                  <Box key={duration} mb={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      {duration.charAt(0).toUpperCase() + duration.slice(1)} Version
                    </Typography>
                    <TextField
                      label="Audio URL"
                      value={spot.audioGuide[selectedLanguage]?.[duration]?.audioUrl || ''}
                      onChange={(e) => handleAudioUrlChange(duration, 'audioUrl', e.target.value)}
                      fullWidth
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Subtitles URL"
                      value={spot.audioGuide[selectedLanguage]?.[duration]?.subtitlesUrl || ''}
                      onChange={(e) => handleAudioUrlChange(duration, 'subtitlesUrl', e.target.value)}
                      fullWidth
                      variant="outlined"
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Internal Comments"
              value={spot.internalComments}
              onChange={(e) => handleChange({ target: { name: 'internalComments', value: e.target.value } })}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
            />
          </Grid>

          {/* Status and Actions */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={spot.isEnabled}
                  onChange={(e) => handleChange({ target: { name: 'isEnabled', value: e.target.checked } })}
                />
              }
              label="Spot Enabled"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/spots')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
              >
                Create Spot
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddSpot;